import { useCallback, useEffect, useState } from "react";
import { Card, DataTable, Badge, Button, Text, Link, EmptyState, BlockStack, InlineStack, Modal, FormLayout, TextField, Select, Banner } from "@shopify/polaris";
import { getRequest, postRequest, putRequest, deleteRequest } from "../../../shared/components/functions";
import ProductSelect from "../../../shared/components/productSelect";

const dateOf = (value) => (value ? String(value).substring(0, 10) : "");
const monthOf = (value) => (value ? String(value).substring(0, 7) : "");
const currentMonth = () => new Date().toISOString().substring(0, 7);
// Machine platforms (Greg/Jack roots + their Schuilenburg sales products): a
// forecast for these needs a structure + variant, or it cannot be exploded.
const MACHINE_PRODUCT_IDS = [833, 834, 10956, 10957];

const emptyForecast = { productId: null, qty: "", periodMonth: "", structureName: "", variantName: "", note: "" };

export function ForecastEditor({ setToast, onChanged }) {
  const [rows, setRows] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(emptyForecast);
  const [editingId, setEditingId] = useState(null);
  const [versions, setVersions] = useState([]);
  const [saving, setSaving] = useState(false);

  const fetchRows = useCallback(async () => {
    const data = await getRequest("/api/mrp/forecast");
    setRows(Array.isArray(data) ? data : []);
    setLoaded(true);
  }, []);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  useEffect(() => {
    async function fetchVersions() {
      if (!editing.structureName) return setVersions([]);
      const data = await getRequest(`/api/${editing.structureName.toLowerCase()}/versions`);
      setVersions(Array.isArray(data) ? data : []);
    }
    fetchVersions();
  }, [editing.structureName]);

  const openNew = () => {
    setEditing({ ...emptyForecast, periodMonth: new Date().toISOString().substring(0, 8) + "01" });
    setEditingId(null);
    setModalOpen(true);
  };

  const openEdit = (row) => {
    setEditing({
      productId: row.ProductId,
      qty: String(row.Qty),
      periodMonth: dateOf(row.PeriodMonth),
      structureName: row.StructureName || "",
      variantName: row.VariantName || "",
      note: row.Note || "",
    });
    setEditingId(row.Id);
    setModalOpen(true);
  };

  const save = async () => {
    setSaving(true);
    const payload = {
      productId: editing.productId,
      qty: Number(editing.qty),
      periodMonth: editing.periodMonth,
      structureName: editing.structureName || null,
      variantName: editing.variantName || null,
      note: editing.note || null,
    };
    if (editingId) await putRequest(`/api/mrp/forecast/${editingId}`, payload, setToast);
    else await postRequest("/api/mrp/forecast", payload, setToast);
    setSaving(false);
    setModalOpen(false);
    await fetchRows();
    if (onChanged) onChanged();
  };

  const remove = async (id) => {
    await deleteRequest(`/api/mrp/forecast/${id}`, setToast);
    await fetchRows();
    if (onChanged) onChanged();
  };

  const isMachineProduct = MACHINE_PRODUCT_IDS.includes(Number(editing.productId));
  const canSave =
    editing.productId &&
    Number(editing.qty) > 0 &&
    editing.periodMonth &&
    (!editing.structureName || editing.variantName) &&
    (!isMachineProduct || (editing.structureName && editing.variantName));

  return (
    <BlockStack gap="400">
      <InlineStack align="space-between" blockAlign="center">
        <Text tone="subdued" as="span">
          Forecast is netted against firm orders per product per month — only the surplus above the order book creates extra demand.
        </Text>
        <Button variant="primary" onClick={openNew}>
          Add forecast
        </Button>
      </InlineStack>

      {loaded && rows.length === 0 ? (
        <Card>
          <EmptyState heading="No forecast yet" image="">
            <p>Add expected build quantities per month (e.g. Greg platform ×25 in September) so long-lead parts get ordered before firm orders exist.</p>
          </EmptyState>
        </Card>
      ) : (
        <Card>
          <DataTable
            verticalAlign="middle"
            increasedTableDensity
            columnContentTypes={["text", "text", "numeric", "text", "text", "text", "text"]}
            headings={[<b>Month</b>, <b>Product</b>, <b>Qty</b>, <b>Structure</b>, <b>Variant</b>, <b>Note</b>, <b>Actions</b>]}
            rows={rows.map((row) => [
              monthOf(row.PeriodMonth) < currentMonth() ? (
                <InlineStack gap="100" blockAlign="center">
                  {monthOf(row.PeriodMonth)}
                  <Badge tone="attention">Ignored — month ended</Badge>
                </InlineStack>
              ) : (
                monthOf(row.PeriodMonth)
              ),
              <Link removeUnderline url={`/products/${row.ProductId}`}>
                {row.ProductNameNL || row.ProductId}
              </Link>,
              row.Qty,
              row.StructureName ? <Badge>{row.StructureName}</Badge> : "",
              row.VariantName || "",
              row.Note || "",
              <InlineStack gap="100">
                <Button size="micro" onClick={() => openEdit(row)}>
                  Edit
                </Button>
                <Button size="micro" tone="critical" onClick={() => remove(row.Id)}>
                  Delete
                </Button>
              </InlineStack>,
            ])}
          />
        </Card>
      )}

      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editingId ? "Edit forecast" : "Add forecast"}
        primaryAction={{ content: "Save", onAction: save, loading: saving, disabled: !canSave }}
        secondaryActions={[{ content: "Cancel", onAction: () => setModalOpen(false) }]}>
        <Modal.Section>
          <FormLayout>
            <div>
              <div style={{ paddingBottom: "5px" }}>Product (use the platform root product for machines)</div>
              <ProductSelect value={editing.productId} onChange={(value) => setEditing((f) => ({ ...f, productId: value }))} />
            </div>
            {isMachineProduct && !(editing.structureName && editing.variantName) && (
              <Banner tone="warning" title="Machine platform — variant required">
                <p>Pick the structure and a representative variant below, otherwise the forecast cannot be exploded into parts and will be ignored.</p>
              </Banner>
            )}
            <FormLayout.Group>
              <TextField
                label="Month"
                type="date"
                autoComplete="off"
                helpText="Any day — stored as that month"
                value={editing.periodMonth}
                onChange={(value) => setEditing((f) => ({ ...f, periodMonth: value }))}
              />
              <TextField label="Quantity" type="number" autoComplete="off" value={String(editing.qty)} onChange={(value) => setEditing((f) => ({ ...f, qty: value }))} />
            </FormLayout.Group>
            <FormLayout.Group>
              <Select
                label="Structure (machines only)"
                value={editing.structureName}
                onChange={(value) => setEditing((f) => ({ ...f, structureName: value, variantName: "" }))}
                options={[
                  { label: "None (flat BOM)", value: "" },
                  { label: "Greg", value: "Greg" },
                  { label: "Jack", value: "Jack" },
                  { label: "GregV3", value: "GregV3" },
                  { label: "JackV3", value: "JackV3" },
                ]}
              />
              <Select
                label="Variant"
                disabled={!editing.structureName}
                value={editing.variantName}
                onChange={(value) => setEditing((f) => ({ ...f, variantName: value }))}
                options={[{ label: editing.structureName ? "Select variant" : "—", value: "" }, ...versions.map((row) => ({ label: row.version, value: row.version }))]}
              />
            </FormLayout.Group>
            <TextField label="Note" autoComplete="off" value={editing.note} onChange={(value) => setEditing((f) => ({ ...f, note: value }))} />
          </FormLayout>
        </Modal.Section>
      </Modal>
    </BlockStack>
  );
}
